import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PageHeader from "@/components/site/PageHeader";
import { AREAS } from "@/lib/content";
import { motion } from "framer-motion";
import { ArrowRight, Check, ExternalLink } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { LANSSTYRELSEN_NP_URL } from "@/components/site/NotariusBanner";

export default function Tjanster() {
    useSEO({
        title: "Rättsområden",
        description:
            "Affärsjuridik, fastighetsrätt, familjerätt, arbetsrätt och brottmål — fem kärnområden där BM Juridik erbjuder spetskompetens.",
    });
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                setTimeout(() => {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 150);
            }
        }
    }, [hash]);

    return (
        <div data-testid="page-tjanster">
            <PageHeader
                kicker="Rättsområden"
                title="Tolv rättsområden,"
                accent="ett ledord — kvalitet."
                description="Vi täcker de flesta juridiska behov — från affärsjuridik och fastighetsrätt till arvsrätt, migrationsrätt och brottmål. Klicka på respektive område för en djupare presentation."
                image="https://images.unsplash.com/photo-1747696766706-5485b39bf358"
            />

            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
                    {AREAS.map((a, i) => {
                        const Icon = a.icon;
                        const reverse = i % 2 === 1;
                        return (
                            <motion.div
                                key={a.id}
                                id={a.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.8 }}
                                data-testid={`tjanst-detail-${a.id}`}
                                className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-start ${
                                    reverse ? "lg:[direction:rtl]" : ""
                                }`}
                            >
                                <div
                                    className={`lg:col-span-5 [direction:ltr] sticky top-28`}
                                >
                                    <div className="w-14 h-14 border border-[#C4A977]/40 grid place-items-center">
                                        <Icon
                                            size={22}
                                            className="text-[#C4A977]"
                                        />
                                    </div>
                                    {a.id === "familjeratt" && (
                                        <div
                                            className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm"
                                            data-testid="familjeratt-gratis-badge"
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#C4A977] animate-pulse" />
                                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                                                Gratis rådgivning vid vårdnadstvist
                                            </span>
                                        </div>
                                    )}
                                    <p className="mt-6 overline">
                                        0{i + 1} / {AREAS.length}
                                    </p>
                                    <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                                        {a.title}
                                    </h2>
                                    <p className="mt-6 text-[#A1A1AA] leading-relaxed">
                                        {a.long}
                                    </p>
                                    {a.id === "notarius-publicus" && (
                                        <a
                                            href={LANSSTYRELSEN_NP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="tjanster-notarius-lansstyrelsen-link"
                                            className="mt-5 inline-flex items-center gap-2 text-sm text-[#C4A977] hover:text-[#DBC08F] transition-colors"
                                        >
                                            Om notarius publicus hos Länsstyrelsen Västra Götaland
                                            <ExternalLink size={13} />
                                        </a>
                                    )}
                                </div>

                                <div className="lg:col-span-7 lg:col-start-7 [direction:ltr] bg-[#111113] border border-white/5 p-10 rounded-sm">
                                    <p className="overline">Vi biträder bl.a. med</p>
                                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {a.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-3"
                                            >
                                                <Check
                                                    size={16}
                                                    className="text-[#C4A977] mt-1 shrink-0"
                                                />
                                                <span className="text-[#F4F4F5] text-sm">
                                                    {b}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="hairline my-8" />
                                    <Link
                                        to="/kontakt"
                                        data-testid={`tjanst-cta-${a.id}`}
                                        className="inline-flex items-center gap-3 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                                    >
                                        Diskutera ett ärende inom {a.title.toLowerCase()}
                                        <ArrowRight
                                            size={14}
                                            className="transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </Link>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
